import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { BrowserMultiFormatReader } from '@zxing/library';

import { convertPersianToEnglish } from '../../../components/converter/index';

const TaskBarcodeScan = ({ detailList = [], onScan }) => {
  const [barcode, setBarcode] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [isStartingScanner, setIsStartingScanner] = useState(false);

  const [scanMessage, setScanMessage] = useState('');
  const [scanStatus, setScanStatus] = useState('');

  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const scanningRef = useRef(false);
  const lastScannedCodeRef = useRef('');

  const handleInputChange = useCallback((newValue) => {
    const value = convertPersianToEnglish(newValue);

    setBarcode(value);
    setScanMessage('');
    setScanStatus('');
  }, []);

  const checkBarcode = useCallback(
    (value = barcode) => {
      const scannedBarcode = convertPersianToEnglish(value).trim();

      if (!scannedBarcode) {
        setScanStatus('error');
        setScanMessage('بارکد را وارد نمایید');

        onScan?.({
          status: 'empty',
          matched: false,
          scannedBarcode: '',
          matchedItem: null,
        });

        return false;
      }

      const detailedItem = detailList.find(
        (item) => scannedBarcode === String(item?.barcode ?? '').trim()
      );

      console.log('MATCHED ITEM:', detailedItem);

      if (!detailedItem) {
        setScanStatus('error');

        setScanMessage('این بارکد در لیست مرسوله‌ها وجود ندارد');

        onScan?.({
          status: 'not_found',
          matched: false,
          scannedBarcode,
          matchedItem: null,
        });

        return false;
      }

      setScanStatus('success');

      setScanMessage('بارکد مرسوله با موفقیت شناسایی شد');

      onScan?.({
        status: 'matched',
        matched: true,
        scannedBarcode,
        matchedItem: detailedItem,
      });

      return true;
    },
    [barcode, detailList, onScan]
  );

  const stopScanner = useCallback(() => {
    scanningRef.current = false;

    try {
      readerRef.current?.reset();
    } catch (error) {
      console.error('Scanner reset error:', error);
    }

    readerRef.current = null;

    if (videoRef.current?.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();

      tracks.forEach((track) => {
        try {
          track.stop();
        } catch (error) {
          console.error('Camera track stop error:', error);
        }
      });

      videoRef.current.srcObject = null;
    }
  }, []);

  const closeScanner = useCallback(() => {
    stopScanner();

    setScannerOpen(false);
    setIsStartingScanner(false);

    lastScannedCodeRef.current = '';
  }, [stopScanner]);

  const handleDecodedBarcode = useCallback(
    (decodedText) => {
      if (!decodedText || !scanningRef.current) {
        return;
      }

      const scannedBarcode = convertPersianToEnglish(decodedText).trim();

      if (!scannedBarcode) {
        return;
      }

      if (lastScannedCodeRef.current === scannedBarcode) {
        return;
      }

      lastScannedCodeRef.current = scannedBarcode;

      setBarcode(scannedBarcode);

      const matched = checkBarcode(scannedBarcode);

      if (matched) {
        setTimeout(() => {
          closeScanner();
        }, 300);
      } else {
        // setTimeout(() => {
        //   lastScannedCodeRef.current = '';
        // }, 800);
        closeScanner();
      }
    },
    [checkBarcode, closeScanner]
  );

  const startScanner = useCallback(async () => {
    if (scanningRef.current) {
      return;
    }

    setIsStartingScanner(true);

    try {
      const reader = new BrowserMultiFormatReader();

      readerRef.current = reader;

      scanningRef.current = true;

      setIsStartingScanner(false);

      await reader.decodeFromVideoDevice(
        undefined,
        videoRef.current,
        (result, error) => {
          if (!scanningRef.current) {
            return;
          }

          if (result) {
            const decodedText = result.getText();

            console.log('ZXING DECODED:', decodedText);

            handleDecodedBarcode(decodedText);
          }
        }
      );
    } catch (error) {
      console.error('Barcode scanner error:', error);

      scanningRef.current = false;

      setIsStartingScanner(false);

      setScanStatus('error');

      setScanMessage('امکان دسترسی به دوربین وجود ندارد');

      try {
        readerRef.current?.reset();
      } catch (resetError) {
        console.error(resetError);
      }

      readerRef.current = null;
      closeScanner();
    }
  }, [handleDecodedBarcode, closeScanner]);

  const openScanner = useCallback(() => {
    setScannerOpen(true);

    setScanMessage('');
    setScanStatus('');
  }, []);

  useEffect(() => {
    if (!scannerOpen) {
      return;
    }

    const timer = setTimeout(() => {
      startScanner();
    }, 150);

    return () => {
      clearTimeout(timer);
    };
  }, [scannerOpen, startScanner]);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, [stopScanner]);

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <TextField
          fullWidth
          variant="outlined"
          label="بارکد مرسوله"
          value={barcode}
          autoComplete="off"
          inputProps={{ style: { direction: 'ltr', textAlign: 'left' } }}
          onChange={(event) => handleInputChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              checkBarcode();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton
                  aria-label="اسکن بارکد با دوربین"
                  onClick={openScanner}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    color: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    '&:hover': {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.16),
                    },
                  }}
                >
                  <CameraAltIcon sx={{ fontSize: 24 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              minHeight: 68,
              paddingInlineStart: '12px',
              paddingInlineEnd: 0,
              borderRadius: '16px',
              bgcolor: 'background.paper',
              fontFamily: 'IRANSans',
              transition: 'box-shadow 180ms ease',
              '& fieldset': {
                borderColor: 'divider',
              },
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused': {
                boxShadow: (theme) =>
                  `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '20px 18px',
              fontSize: '16px',
            },
            '& .MuiInputLabel-root': {
              fontFamily: 'IRANSans',
            },
          }}
        />

        {scanMessage && (
          <Alert
            severity={scanStatus === 'success' ? 'success' : 'error'}
            icon={
              scanStatus === 'success' ? (
                <CheckCircleIcon />
              ) : (
                <ErrorOutlineIcon />
              )
            }
            sx={{
              mt: 1.5,
              borderRadius: '12px',
              fontFamily: 'IRANSans',
              direction: 'rtl',
            }}
          >
            {scanMessage}
          </Alert>
        )}

        <Button
          fullWidth
          onClick={() => checkBarcode()}
          disabled={!barcode}
          variant="contained"
          sx={{
            width: '100%',
            height: '57px',
            borderRadius: '16px',
            fontFamily: 'IRANSans',
            fontSize: '16px',
            fontWeight: '700',
            marginTop: '16px',
            color: 'white',
          }}
        >
          بررسی بارکد
        </Button>
      </Box>

      <Dialog
        open={scannerOpen}
        onClose={closeScanner}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
            background: '#000',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: '#000',
            color: '#fff',
            fontFamily: 'IRANSans',
            fontSize: '18px',
            fontWeight: 700,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            direction: 'rtl',
          }}
        >
          اسکن بارکد
          <IconButton
            onClick={closeScanner}
            sx={{
              color: '#fff',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: 0,
            background: '#000',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '1 / 1',
              background: '#000',
              overflow: 'hidden',
            }}
          >
            <video
              ref={videoRef}
              muted
              playsInline
              autoPlay
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                background:
                  'linear-gradient(90deg, rgba(0,0,0,.45) 0%, transparent 20%, transparent 80%, rgba(0,0,0,.45) 100%)',
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                left: '12%',
                right: '12%',
                top: '25%',
                height: '50%',
                border: '2px solid rgba(255,255,255,0.8)',
                borderRadius: '16px',
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: -2,
                  top: -2,
                  width: 35,
                  height: 35,
                  borderLeft: '4px solid #fff',
                  borderTop: '4px solid #fff',
                  borderTopLeftRadius: '12px',
                }}
              />

              <Box
                sx={{
                  position: 'absolute',
                  right: -2,
                  top: -2,
                  width: 35,
                  height: 35,
                  borderRight: '4px solid #fff',
                  borderTop: '4px solid #fff',
                  borderTopRightRadius: '12px',
                }}
              />

              <Box
                sx={{
                  position: 'absolute',
                  left: -2,
                  bottom: -2,
                  width: 35,
                  height: 35,
                  borderLeft: '4px solid #fff',
                  borderBottom: '4px solid #fff',
                  borderBottomLeftRadius: '12px',
                }}
              />

              <Box
                sx={{
                  position: 'absolute',
                  right: -2,
                  bottom: -2,
                  width: 35,
                  height: 35,
                  borderRight: '4px solid #fff',
                  borderBottom: '4px solid #fff',
                  borderBottomRightRadius: '12px',
                }}
              />

              <Box
                sx={{
                  position: 'absolute',
                  left: '4%',
                  right: '4%',
                  height: '2px',
                  background: '#ff1744',
                  boxShadow:
                    '0 0 8px rgba(255,23,68,0.9), 0 0 16px rgba(255,23,68,0.6)',
                  animation: 'scannerLine 2s ease-in-out infinite',

                  '@keyframes scannerLine': {
                    '0%': {
                      top: '8%',
                    },
                    '50%': {
                      top: '92%',
                    },
                    '100%': {
                      top: '8%',
                    },
                  },
                }}
              />
            </Box>

            {isStartingScanner && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  fontFamily: 'IRANSans',
                  gap: 2,
                }}
              >
                <CircularProgress
                  sx={{
                    color: '#fff',
                  }}
                />

                <Typography
                  sx={{
                    fontFamily: 'IRANSans',
                    fontSize: '15px',
                  }}
                >
                  در حال فعال‌سازی دوربین...
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              padding: '18px 20px 22px',
              background: '#000',
              color: '#fff',
              textAlign: 'center',
              direction: 'rtl',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'IRANSans',
                fontSize: '15px',
                fontWeight: 500,
              }}
            >
              بارکد مرسوله را داخل کادر قرار دهید
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TaskBarcodeScan;
